// dev-shop-my-ride — PDP vehicle-fitment selector controller.
// ───────────────────────────────────────────────────────────────────────────
// Mounted by blocks/dev-shop-my-ride.liquid as <dev-shop-my-ride>. Builds a
// Type → Year → Make → Model cascade from the per-product compatible-vehicles
// matrix (data-smr-fitment: {options, fitment(rows), str}). Each fitment row is
// indexed by option position; values are indices into `str` (the `year` slot may
// be an array → multiple years), and the `variant_id` slot holds the Shopify
// variant ID. The cascade only ever offers values that still have a matching row, so
// there are no incompatible dead-ends (mirrors assets/fitment.js getYMMValueByIndex).
//
// The fitment cascade and Shopify's native product options are mutually
// exclusive, never shown together: native <variant-picker> is visible ONLY in
// the intro state (before the shopper commits to either flow) — Horizon's own
// product-form-component already gates Add-to-Cart correctly off it, so we
// don't touch ATC state at all here. The moment a ride starts ("Filter By
// Ride" / "Choose From Garage" / an incoming deep link), the native picker is
// hidden and WE own ATC: forced disabled through the quiz, forced enabled once
// a ride resolves to a known-good variant (selected in the native picker by
// dispatching its native change, so price/media/buy-button update normally).
//
// Matching a known vehicle (incoming deep link, garage) is ID-first:
// `data-smr-fitment`'s `vehicle_ids` (+ this product's `sbv_fitments`)
// give a canonical, catalog-wide vehicle identity, resolved without ever
// string-comparing labels against this one product's own `str` table — see
// #resolveVehicleFitments. When it resolves, #loadRide scopes the whole cascade
// to just those confirmed-compatible rows (#activeEntries) so a level with more
// than one remaining option (e.g. Model, when a generic requested vehicle maps
// to several real SKUs) still renders as a real, correctly-filtered dropdown
// rather than an instant complete. Falls back to #rideMatchByLabel (string
// matching against the full per-product table) only when the vehicle isn't in
// vehicle_ids at all, mirroring the legacy fitment.js reference
// (resolveVehicleFitments → loadFitmentByVehicle).

(function () {
  'use strict';

  const NAME = 'dev-shop-my-ride';
  if (customElements.get(NAME)) return;

  const YMM_TAGS = ['type', 'year', 'make', 'model'];
  // Real fitment metafields label the Type/Make/Model columns with assorted tag
  // variants; canonicalise them so year-sorting + ride-matching keep working. The
  // cascade SET itself is chosen positionally (see #parseData), so an unlisted tag
  // never drops a column — this map only normalises names it recognises.
  const YMM_ALIAS = {
    vehicle_type: 'type', vehicletype: 'type', vehicle: 'type',
    years: 'year', yr: 'year',
    brand: 'make', manufacturer: 'make', mfg: 'make', mfr: 'make', vendor: 'make',
    model_name: 'model', modelname: 'model', vehicle_model: 'model', trim: 'model',
  };
  const LEVEL_LABELS = { type: 'Vehicle Type', year: 'Year', make: 'Make', model: 'Model' };
  const GUARANTEED_TEXT = 'Guaranteed fit for';
  const GARAGE_KEY = 'devGarage';

  const q = (root, sel) => root.querySelector(sel);
  const escapeHtml = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  // vehicle-type key for the garage rows. The silhouette art itself is cloned from
  // the <template data-smr-veh="…"> set rendered by snippets/dev-vehicle-icons.liquid
  // (see #iconHtml) — keeps the large SVG art out of this module.
  function vehicleType(parts) {
    const type = (parts && parts[0] && String(parts[0].value).toLowerCase()) || '';
    if (type.includes('snow')) return 'snowmobile';
    // No dedicated Side-by-Side silhouette exists yet — reuse the ATV one
    // (both are 4-wheel/off-road), rather than falling through to the
    // dirt-bike-looking default below.
    if (type.includes('atv') || type.includes('quad') || type.includes('side')) return 'atv';
    if (type.includes('motorcycle') || type.includes('street') || type.includes('dual')) return 'moto';
    return 'atv';
  }

  // Coarse ride family for TYPE matching. The vehicle picker emits a DETAILED
  // type category ("Motorcycle - Dual-Sport"), while a product's fitment table
  // stores a COARSE type ("Motorcycle"). Exact string match fails across that
  // granularity gap, so the type level (see #typeStrIndex) classifies both sides
  // through this and matches on family. Mirrors marqo.js __ymmRideType (which the
  // collection uses to scope the results grid) — kept local since it isn't exported.
  function rideFamily(v) {
    const t = String(v || '').toLowerCase();
    if (/atv|utv|sxs|side by side|quad/.test(t)) return 'atv';
    if (/snow|sled/.test(t)) return 'snowmobile';
    if (/dirt/.test(t)) return 'dirtbike';
    if (/motor|street|cruiser|road|dual|adventure|touring|scooter|moped/.test(t)) return 'motorcycle';
    if (/bicycle|e-?bike|mtb|pedal/.test(t)) return 'bicycle';
    return t; // unknown → keep raw so it only matches an identical raw type
  }

  customElements.define(
    NAME,
    class extends HTMLElement {
      #initialized = false;
      #cfg = null;
      #variants = [];
      #entries = [];
      #activeEntries = null; // vehicle_ids- or vehicle_fitments-confirmed row subset; null = no known vehicle, use #entries
      // True for the whole duration of a #loadRide()-driven session (deep link, garage —
      // vehicle_ids-confirmed OR label-matched fallback, both count) OR an
      // #applyFitVehicle()-driven session (fit_vehicle_id/vehicle_fitments URL match),
      // false for a manual #startRide() walkthrough. Gates #advanceTo's auto-select: picking a
      // single-option step FOR the shopper is only safe once a real vehicle is already
      // confirmed — during a from-scratch manual build it would silently choose values
      // the shopper never actually saw or agreed to.
      #knownVehicle = false;
      #str = [];
      #levels = []; // [{ oi, tag, label }] — one per YMM accordion
      #variantOi = -1; // option position holding the Shopify variant ID
      #notesOi = -1; // option position holding the fitment note (RDI: optionIndices["Notes"])
      #selected = {}; // option-index → str-index
      #steps = []; // accordion DOM, one per level
      #lockedSteps = new Set(); // step indices auto-selected AND hide_singles → no manual reopen
      #view = 'intro';
      #vehicleName = '';
      #resolvedSku = ''; // set by #complete() — the human part number for the resolved ride, used by #addToGarage
      #resolvedAvailable = false; // set by #complete() — whether the resolved variant is actually purchasable, gates ATC
      #resolvedVariantId = null; // set by #complete() — the target variant id #applyGate confirms the native form has actually caught up to before enabling ATC
      #statusDefault = '';
      #garageSelIdx = -1;
      #garageRows = [];
      #findVehicles = [];
      #vehTpls = {};
      #fitVehicleId = null; // vehicle id from the fit-{vehicleId}-{slug} URL (data island "fit_vehicle_id")
      #vehicleFitments = null; // vehicle id → array of fitment_data_v2 row indexes (data island "vehicle_fitments")
      #vehicleIds = null; // { str, vehicle_ids } — data island "vehicle_ids"; canonical vehicle lookup
      #onVariantUpdate = null;
      #onKeydown = null;

      connectedCallback() {
        if (this.#initialized) return;
        this.#initialized = true;

        try {
          this.#parseData();
        } catch (e) {
          console.error(`[${NAME}] bad fitment data`, e);
          this.style.display = 'none';
          return;
        }
        if (!this.#levels.length || !this.#entries.length) {
          this.style.display = 'none';
          return;
        }

        this.#cacheNodes();
        this.#buildSteps();
        this.#bind();
        this.#gateAtcOnLoad(); // keep ATC disabled until a valid selection is made
        this.#renderView('intro');
        this.#refreshGarageButton();
        // The /products/{handle}/fit-{vehicleId} PATH match wins first (Revco's
        // own mechanism, most explicit); then ?fit={fitmentKey}-{year} (RDI
        // parity); then a vehicle carried over from a filtered collection
        // (query / referrer). If a URL somehow carries both
        // (/fit-xxx?fit=yyy), the path wins and ?fit is never even consulted.
        if (!this.#applyFitVehicle() && !this.#applyFitParam()) this.#applyIncomingRide();
      }

      // When the URL is /products/{handle}/fit-{vehicleId}-{slug} (see
      // blocks/dev-shop-my-ride.liquid's fit_vehicle_id), fitment.vehicle_fitments
      // maps that vehicle id straight to the array of fitment_data_v2 ROW INDEXES
      // this product has confirmed for it. Unlike the garage/deep-link paths
      // (#loadRide → #resolveVehicleFitments), there's no compatibility check to
      // run here — the metafield only has an entry for this vehicle at all when
      // it's already known to fit this exact product — so the rows are used
      // directly as #activeEntries and handed to #advanceTo, same as #loadRide's
      // "resolved" branch (hide_singles auto-selects/locks exactly as in a
      // vehicle search session). Indexed into the RAW fitment config (not
      // #entries), matching #resolveVehicleFitments's fitmentByKey lookup — keeps
      // this correct whether fitment_data_v2.fitment is a JSON array or an
      // object keyed by row index.
      // Returns true when a matching vehicle was found and applied.
      #applyFitVehicle() {
        const id = this.#fitVehicleId;
        if (!id || !this.#vehicleFitments) return false;
        const indexes = this.#vehicleFitments[id];
        if (!Array.isArray(indexes) || !indexes.length) return false;
        const fitmentByKey = this.#cfg.fitment;
        const rows = indexes.map((i) => fitmentByKey && fitmentByKey[i]).filter(Boolean);
        if (!rows.length) return false;

        this.#activeEntries = rows;
        this.#knownVehicle = true;
        this.#selected = {};
        this.#resetLocks();
        this.#syncCartProperty(null);
        this.#setNote(null);
        this.#steps.forEach((_, k) => this.#closeStep(k));
        this.#advanceTo(0, 0);
        return true;
      }

      // RDI parity — ?fit={fitmentKey}-{year}, e.g. ?fit=42-2023. Purely a URL query
      // param (never a page/Liquid concern — no title/canonical/meta tag changes,
      // mirroring RDI's fitment.js#preselectFromFitParam exactly), and a DIFFERENT
      // key space from #applyFitVehicle's path-based fit_vehicle_id above:
      // fitmentKey here is this product's own fitment_data_v2.fitment row key (not
      // a portable/global vehicle id), so it's looked up the same way
      // #resolveVehicleFitments does — directly against the RAW #cfg.fitment
      // object, not the possibly-reindexed #entries array. year is the LITERAL
      // selected year string, not a str-index — it disambiguates which exact year
      // a multi-year row resolved to, overriding that slot instead of decompressing
      // it, same as RDI. Only the first two hyphen-delimited segments are used
      // (RDI's own `[rawKey, rawYear] = fitParam.split('-')` silently drops
      // anything past the 2nd segment; matched here for parity).
      //
      // Deliberately NOT handed to #loadRide — this flow behaves differently by
      // design: every level is force-filled straight from the matched row (never
      // left as an open real choice, unlike #loadRide's "resolved" branch, which
      // only auto-fills a level when just ONE compatible option remains), and no
      // step ever gets locked (the shopper can freely reopen and change any of
      // them). #activeEntries stays null (rather than scoped to this one row), so
      // a reopened dropdown searches the full per-product table — still filtered
      // top-down by #matchesUpto (see #valuesForStep), just not restricted to only
      // this one matched row. #complete() still runs at the end so variant resolution /
      // ATC gating behave normally once everything's filled in.
      // Returns true when a matching row was found and applied.
      #applyFitParam() {
        let fitParam;
        try {
          fitParam = new URLSearchParams(location.search).get('fit');
        } catch (e) {
          return false;
        }
        if (!fitParam) return false;
        const [rawKey, rawYear] = fitParam.split('-');
        const fitmentKey = String(rawKey || '').trim();
        const yearValue = String(rawYear || '').trim();
        if (!fitmentKey || !yearValue) return false;

        const fitmentByKey = this.#cfg.fitment;
        const row = fitmentByKey && fitmentByKey[fitmentKey];
        if (!row) return false;

        const yearLevel = this.#levels.find((L) => L.tag === 'year');
        const yearIdx = this.#strIndex(yearValue);
        const selections = {};
        this.#levels.forEach((L) => {
          if (yearLevel && L.oi === yearLevel.oi) {
            if (yearIdx >= 0) selections[L.oi] = yearIdx;
            return;
          }
          const v = row[L.oi];
          const idx = Array.isArray(v) ? v[0] : v;
          if (idx != null) selections[L.oi] = idx;
        });
        if (!this.#levels.every((L) => selections[L.oi] != null)) return false;

        this.#activeEntries = null;
        this.#knownVehicle = true;
        this.#selected = selections;
        this.#resetLocks();
        this.#syncCartProperty(null);
        this.#setNote(null);
        this.#steps.forEach((_, k) => this.#closeStep(k));
        this.#complete();
        return true;
      }

      // When the shopper arrives from a vehicle-filtered collection, the vehicle is
      // carried as filter.fitments=Type>Year>Make>Model. If this product fits it,
      // drop straight into that ride (guaranteed → complete; partial → resume the
      // cascade); if it doesn't fit, leave the intro state untouched.
      #applyIncomingRide() {
        const fit = this.#incomingFitment();
        if (!fit) return;
        // The deep link is ALWAYS ordered Type>Year>Make>Model. Map each value onto the
        // product's fitment level at the SAME position. Keep parts LEVEL-LENGTH and
        // aligned (do NOT filter — that would shift positions); levels past the incoming
        // YMM (e.g. Colour, Position) simply get no value and are left for the shopper.
        const vals = fit.split('>').map((v) => String(v).trim());
        if (!vals.some(Boolean)) return;
        const parts = this.#levels.map((L, i) => ({ value: vals[i] }));
        if (this.#rideStatus(parts) === 'incompatible') return; // this product doesn't fit that vehicle
        this.#loadRide(parts);
      }

      // Resolve the incoming vehicle deep link (filter.fitments=Type>Year>Make>Model).
      // Prefer the PDP's own URL — the collection carries the vehicle onto product-card
      // links — then fall back to the referrer's query. Same-origin collection→product
      // navigation sends the full referrer (incl. query), so the ride still pre-fills
      // when the link didn't carry the param; this mirrors the legacy fitment.js, which
      // reads the vehicle straight off document.referrer.
      #incomingFitment() {
        const KEY = 'filter.fitments';
        const read = (search) => {
          try { return new URLSearchParams(search).get(KEY); } catch (e) { return null; }
        };
        const here = read(location.search);
        if (here) return here;
        const ref = document.referrer;
        if (!ref) return null;
        try {
          const u = new URL(ref);
          if (u.origin !== location.origin) return null; // only trust a same-origin referrer
          return read(u.search);
        } catch (e) { return null; }
      }

      // <add-to-cart-component> / ProductForm / <variant-picker> may mount after us,
      // so re-assert the native-picker visibility + gate on the next frame and
      // shortly after as a backstop.
      #gateAtcOnLoad() {
        this.#integratePdp();
        requestAnimationFrame(() => this.#integratePdp());
        setTimeout(() => this.#integratePdp(), 400);
      }

      #integratePdp() {
        this.#syncNativePicker(this.#view === 'intro');
        this.#applyGate();
      }

      // Native <variant-picker> vs our own fitment cascade are mutually exclusive —
      // visible only in intro, hidden the instant a ride starts (through completion).
      // Kept in the DOM either way (never removed) — #selectVariantById still
      // dispatches change on its controls to update price/media/buy-button.
      // Scoped to this section, not document-wide — a recommendations/upsell
      // carousel elsewhere on the page can render its OWN variant-picker for a
      // DIFFERENT product; a global querySelector could silently hide the wrong one.
      #syncNativePicker(visible) {
        const scope = this.closest('.shopify-section') || document;
        const vp = scope.querySelector('variant-picker');
        if (!vp) return;
        vp.style.display = visible ? '' : 'none';
      }

      // On returning to intro (#clear), re-trigger the native picker's own
      // reactivity so Horizon's product-form-component re-asserts ATC state for
      // whatever's ACTUALLY selected there — we may have forced it disabled/enabled
      // while a ride was active, and nothing else will naturally re-sync it since
      // #applyGate no longer touches ATC at all once back in intro.
      #resyncNativeAtc() {
        const scope = this.closest('.shopify-section') || document;
        const vp = scope.querySelector('variant-picker');
        if (!vp) return;
        const checked = vp.querySelector('input:checked') || vp.querySelector('select');
        if (checked) checked.dispatchEvent(new Event('change', { bubbles: true }));
      }

      disconnectedCallback() {
        document.removeEventListener('variant:update', this.#onVariantUpdate);
        document.removeEventListener('keydown', this.#onKeydown);
      }

      // ── data ─────────────────────────────────────────────────────────────
      #parseData() {
        const data = JSON.parse(q(this, '[data-smr-fitment]').textContent);
        this.#cfg = data.fitment || {};
        this.#fitVehicleId = data.fit_vehicle_id != null ? String(data.fit_vehicle_id) : null;
        this.#vehicleFitments = data.vehicle_fitments || null;
        this.#vehicleIds = data.vehicle_ids || null;
        const vEl = q(this, '[data-smr-variants]');
        this.#variants = vEl ? JSON.parse(vEl.textContent) || [] : [];

        const options = this.#cfg.options || [];
        this.#str = this.#cfg.str || [];
        const fitment = this.#cfg.fitment;
        this.#entries = Array.isArray(fitment) ? fitment : Object.values(fitment || {});

        // Locate the variant_id column case-insensitively; fall back to the documented
        // layout ([...YMM, variant_id, notes]) when the tag is absent.
        const norm = (t) => String(t == null ? '' : t).trim().toLowerCase();
        this.#variantOi = options.findIndex((o) => norm(o.tag) === 'variant_id');
        if (this.#variantOi < 0 && options.length) this.#variantOi = options.length - 2; // getVariantSKU: data[len-2]

        // Fitment note (RDI: optionIndices["Notes"]): a per-row caveat (e.g. "Requires
        // additional hardware") shown once a ride resolves. Tag-matched ("note",
        // singular — the metafield's actual, consistent tag) — same approach as the
        // variant_id lookup above. Positional fallback (documented
        // [...YMM, variant_id, notes] layout) as a last resort.
        this.#notesOi = options.findIndex((o) => norm(o.tag) === 'note');
        if (this.#notesOi < 0 && this.#variantOi >= 0 && this.#variantOi + 1 < options.length) {
          this.#notesOi = this.#variantOi + 1;
        }

        // The cascade = the YMM columns that PRECEDE the variant_id column. Everything
        // from variant_id onward (the variant ID + the trailing notes column) is tail metadata,
        // never a selectable level — so notes never shows, regardless of how it's tagged.
        // Taking the set POSITIONALLY (not via an exact tag whitelist) also keeps the full
        // Type→Year→Make→Model cascade when a product's real fitment metafield tags those
        // columns with variants ("Vehicle Type", "Brand", …); the old
        // `YMM_TAGS.includes(tag)` filter silently dropped any column whose tag didn't
        // match verbatim, leaving just the ones that happened to (e.g. only "Year").
        this.#levels = options
          .slice(0, this.#variantOi < 0 ? 0 : this.#variantOi)
          .map((o, oi) => {
            const tag = YMM_ALIAS[norm(o.tag)] || norm(o.tag); // canonicalise for year-sort / ride-match
            // hide_singles: per-level flag from fitment_data_v2.options — when an
            // auto-selected single option should LOCK the step (no dropdown affordance)
            // rather than just fill it in. Real data has this true for Type/Year/Make
            // and false for Model, matching the reference widget (RDI): a vehicle's
            // Type/Year/Make stay locked once resolved, while Model — which a single
            // canonical vehicle can still map to several real SKUs — stays interactive.
            return { oi, tag, label: LEVEL_LABELS[tag] || o.label || o.tag || tag, hideSingles: !!o.hide_singles };
          });
      }

      #cacheNodes() {
        this.titleEl = q(this, '[data-smr-title]');
        this.statusText = q(this, '[data-smr-status-text]');
        this.introEl = q(this, '[data-smr-intro]');
        this.cascadeEl = q(this, '[data-smr-cascade]');
        this.hintEl = q(this, '[data-smr-hint]');
        this.rideLinksEl = q(this, '[data-smr-ride-links]');
        this.completeEl = q(this, '[data-smr-complete]');
        this.noteEl = q(this, '[data-smr-note]');
        this.noteBodyEl = q(this, '[data-smr-note-body]');
        this.noteToggleEl = q(this, '[data-smr-note-toggle]');
        this.shopRideEl = q(this, '[data-smr-shop-ride]');
        this.garageBtn = q(this, '[data-smr-choose-garage]');
        this.stepTpl = q(this, '[data-smr-step-tpl]');
        this.modalEl = q(this, '[data-smr-garage-modal]');
        this.garageRowsEl = q(this, '[data-smr-garage-rows]');
        this.garageSubmitEl = q(this, '[data-smr-garage-submit]');
        this.garageRowTpl = q(this, '[data-smr-garage-row-tpl]');
        this.findModalEl = q(this, '[data-smr-find-modal]');
        this.findRowsEl = q(this, '[data-smr-find-rows]');
        this.findRowTpl = q(this, '[data-smr-find-row-tpl]');
        this.findAddBtn = q(this, '[data-smr-find-add]');
        this.#statusDefault = this.statusText ? this.statusText.textContent : '';
        // Cache the vehicle silhouette templates (rendered by dev-vehicle-icons).
        this.querySelectorAll('[data-smr-veh]').forEach((t) => {
          this.#vehTpls[t.dataset.smrVeh] = t.innerHTML;
        });
      }

      // Silhouette markup for a ride, cloned from the rendered <template> set.
      #iconHtml(parts) {
        return this.#vehTpls[vehicleType(parts)] || this.#vehTpls.bike || '';
      }

      // ── accordion construction ───────────────────────────────────────────
      #buildSteps() {
        this.#levels.forEach((lvl, i) => {
          const node = this.stepTpl.content.firstElementChild.cloneNode(true);
          node.dataset.index = String(i);
          q(node, '[data-smr-step-label]').textContent = lvl.label;
          q(node, '[data-smr-step-toggle]').addEventListener('click', () => this.#toggleStep(i));
          this.cascadeEl.appendChild(node);
          this.#steps.push(node);
        });
      }

      #bind() {
        q(this, '[data-smr-filter-by-ride]').addEventListener('click', () => this.#startRide());
        q(this, '[data-smr-clear]').addEventListener('click', () => this.#clearOrLeaveFitUrl());
        q(this, '[data-smr-add-garage]').addEventListener('click', () => this.#addToGarage());
        if (this.garageBtn) this.garageBtn.addEventListener('click', () => this.#chooseFromGarage());
        if (this.noteToggleEl) this.noteToggleEl.addEventListener('click', () => this.#toggleNote());
        // "Can't Find Your Vehicle?" opens the shared vehicle-request form (emails customer
        // service). The button carries [data-dev-vehicle-request-open], so the site-wide
        // <dev-vehicle-request> controller (snippets/dev-vehicle-request.liquid, rendered in
        // the header) handles it — no binding here. My Garage is still reached via the header
        // garage button and the "Add To Your Garage" flow.

        // garage popup: close (X / scrim / Esc) + confirm
        this.modalEl.querySelectorAll('[data-smr-garage-close]').forEach((el) => el.addEventListener('click', () => this.#closeGarageModal()));
        this.garageSubmitEl.addEventListener('click', () => this.#confirmGarage());

        // "My Garage" management popup: close + add ride
        this.findModalEl.querySelectorAll('[data-smr-find-close]').forEach((el) => el.addEventListener('click', () => this.#closeFindModal()));
        this.findAddBtn.addEventListener('click', () => this.#addYourRide());

        this.#onKeydown = (e) => {
          if (e.key !== 'Escape') return;
          if (!this.modalEl.hidden) this.#closeGarageModal();
          if (!this.findModalEl.hidden) this.#closeFindModal();
        };
        document.addEventListener('keydown', this.#onKeydown);

        // Re-assert our view-based gate after Horizon's ProductForm toggles ATC on
        // variant:update (e.g. when we programmatically select the resolved variant).
        this.#onVariantUpdate = () => {
          setTimeout(() => this.#applyGate(), 0);
        };
        document.addEventListener('variant:update', this.#onVariantUpdate);
      }

      // ── cascade ──────────────────────────────────────────────────────────
      #matchesUpto(entry, uptoLevel) {
        for (let k = 0; k < uptoLevel; k++) {
          const oi = this.#levels[k].oi;
          const v = this.#selected[oi];
          if (v == null) return false;
          if (![entry[oi]].flat(2).includes(v)) return false;
        }
        return true;
      }

      // The str indices still available at step i given the current selection —
      // i.e. the values that keep at least one fitment row matchable. Shared by
      // #populateStep (to render) and #advanceTo (to detect single-option steps).
      // Scoped to #activeEntries (the vehicle_ids-confirmed row subset) once a
      // known vehicle has been resolved, so every level — not only the ones
      // that happen to be unanimous — only ever offers options THAT vehicle
      // actually supports; otherwise falls back to the full per-product table,
      // still filtered top-down by #matchesUpto (Type constrains Year, Year
      // constrains Make, etc.) — that hierarchy always applies regardless of
      // #activeEntries; only the SEARCH SPACE (this product's full table vs. a
      // known vehicle's narrower row subset) changes. #applyFitParam (?fit=
      // sessions) relies on exactly this: it sets #activeEntries to null so
      // reopening any dropdown searches the full table rather than being
      // restricted to one specific matched row, while still respecting the
      // normal cascade hierarchy.
      #valuesForStep(i) {
        const oi = this.#levels[i].oi;
        const vals = new Set();
        (this.#activeEntries || this.#entries).forEach((e) => {
          if (this.#matchesUpto(e, i)) [e[oi]].flat(2).forEach((v) => vals.add(v));
        });
        return [...vals];
      }

      // Move the cascade to step i, auto-selecting and skipping any step (at or past
      // `autoFrom`) that has only ONE available option AND is flagged hide_singles —
      // repeating until a step needs a real choice, or every level is resolved →
      // complete. hide_singles (per-level, from fitment_data_v2.options) is the sole
      // signal for whether a single-option step should be silently pre-filled — real
      // data has this true for Type/Year/Make, false for Model/Position/Size (see
      // #parseData) — a false step ALWAYS waits for an explicit click even with only
      // one option listed, so the shopper still sees/confirms it.
      //
      // Also gated on #knownVehicle (set by #loadRide): auto-picking a single-option
      // step during a from-scratch manual build (#startRide) can mislead the shopper
      // into a part they never actually chose or saw, regardless of hide_singles — so
      // nothing auto-selects there at all.
      //
      // `autoFrom` guards the levels the shopper EXPLICITLY requested (the deep-link /
      // garage YMM). If one of those didn't match, its step may still collapse to a
      // single option — but that option is a DIFFERENT vehicle than requested (e.g.
      // asked "Honda", product only fits "Husqvarna"). Auto-selecting it would silently
      // swap the vehicle and falsely report "Guaranteed fit", so requested levels always
      // open as a real choice (→ partial). Only levels the shopper did NOT specify
      // (index ≥ autoFrom) auto-select. Defaults to 0 (manual build → skip all singles).
      #advanceTo(i, autoFrom = 0) {
        while (i >= 0 && i < this.#levels.length) {
          const vals = this.#valuesForStep(i);
          if (vals.length === 1 && i >= autoFrom && this.#knownVehicle && this.#levels[i].hideSingles) {
            this.#selected[this.#levels[i].oi] = vals[0];
            this.#lockStep(i, true); // hide_singles just gated the auto-select itself — always lock what it fills in
            this.#updateVehicleInfoLinks();
            i = this.#firstUnselected();
            continue;
          }
          this.#renderView('ride'); // real choice needed → stay in the quiz (ATC gated)
          this.#populateStep(i);
          this.#openStep(i);
          this.#setHint(i);
          return;
        }
        this.#complete(); // no step left needing a choice
      }

      // Lock/unlock a step's toggle so a hide_singles auto-selection can't be manually
      // reopened.
      #lockStep(i, locked) {
        const node = this.#steps[i];
        if (!node) return;
        if (locked) this.#lockedSteps.add(i); else this.#lockedSteps.delete(i);
        node.classList.toggle('is-locked', locked);
        const toggle = q(node, '[data-smr-step-toggle]');
        if (toggle) toggle.disabled = locked;
      }

      // Clear all step locks — called whenever a fresh ride starts (#loadRide, #clear,
      // #startRide) so a lock from a PREVIOUS vehicle never lingers.
      #resetLocks() {
        this.#lockedSteps.clear();
        this.#steps.forEach((_, k) => this.#lockStep(k, false));
      }

      #populateStep(i) {
        const arr = this.#valuesForStep(i).map((v) => ({ v, label: String(this.#str[v]) }));
        arr.sort((a, b) => this.#sort(a.label, b.label, this.#levels[i].tag === 'year'));

        const list = q(this.#steps[i], '[data-smr-step-list]');
        list.innerHTML = '';
        arr.forEach(({ v, label }) => {
          const li = document.createElement('li');
          li.className = 'dev-smr__opt';
          li.setAttribute('role', 'option');
          li.tabIndex = 0;
          li.textContent = label;
          const pick = () => this.#selectLevel(i, v);
          li.addEventListener('click', pick);
          li.addEventListener('keydown', (ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              pick();
            }
          });
          list.appendChild(li);
        });
      }

      #selectLevel(i, strVal) {
        this.#selected[this.#levels[i].oi] = strVal;
        for (let k = i + 1; k < this.#levels.length; k++) delete this.#selected[this.#levels[k].oi]; // reset downstream
        this.#setNote(null); // this selection just invalidated the old resolved combo — #complete() re-sets it if the new one resolves
        this.#updateVehicleInfoLinks();
        this.#closeStep(i);
        this.#paintSteps();

        const next = this.#firstUnselected();
        if (next < 0) {
          this.#complete();
          return;
        }
        this.#advanceTo(next); // auto-skips any single-option step from here on
      }

      #complete() {
        // Prefer #activeEntries (vehicle_ids-confirmed rows) when a known vehicle is
        // active, so the resolved variant is one the fitment feed actually confirmed for
        // it — not just any row in the full table that happens to share these values.
        const e = (this.#activeEntries || this.#entries).find((en) => this.#levels.every((L) => [en[L.oi]].flat(2).includes(this.#selected[L.oi])));
        const variantId = e && this.#variantOi >= 0 ? String(this.#str[e[this.#variantOi]]) : null;
        this.#vehicleName = this.#levels.map((L) => String(this.#str[this.#selected[L.oi]])).join(' ');
        this.#resolvedSku = '';
        this.#resolvedAvailable = false;
        this.#resolvedVariantId = null;
        const variant = variantId ? this.#variantById(variantId) : null;
        if (variant) {
          this.#resolvedSku = variant.sku || '';
          this.#resolvedAvailable = !!variant.available;
          this.#resolvedVariantId = variant.id;
          this.#selectVariantById(variant.id);
        }
        this.#setNote(e && this.#notesOi >= 0 ? this.#str[e[this.#notesOi]] : null);
        this.#buildShopUrl();
        this.#syncCartProperty(this.#vehicleName); // carry the confirmed ride onto the cart line (matches RDI)
        this.#renderView('complete'); // last step → ATC active again
      }

      // Per-row fitment caveat (RDI's setFitmentNote / .jsFitmentNote), e.g. "Requires
      // additional hardware, sold separately." Hidden whenever there's nothing to show,
      // rather than left empty — matches every other optional row in this widget.
      //
      // Some Notes rows carry a full product description rather than a one-line
      // caveat, which used to push Add To Cart far down the page (previously
      // "fixed" by a CSS rule that only hid markup past the first <p>/<ul> —
      // fragile, and did nothing for a single long paragraph or any other shape).
      // .dev-smr__note-body is height-clamped in CSS regardless of markup shape;
      // here we measure the rendered content against that clamp and only reveal
      // the toggle button when it actually overflows, so short notes still render
      // exactly as before with no button.
      #setNote(value) {
        if (!this.noteEl) return;
        const text = value == null ? '' : String(value).trim();
        this.noteEl.classList.remove('has-more', 'is-expanded');
        this.noteEl.hidden = !text;
        if (!this.noteBodyEl) return;
        this.noteBodyEl.innerHTML = text;
        if (!text || !this.noteToggleEl) return;
        this.noteToggleEl.hidden = true;
        requestAnimationFrame(() => {
          const overflows = this.noteBodyEl.scrollHeight > this.noteBodyEl.clientHeight + 1;
          this.noteEl.classList.toggle('has-more', overflows);
          this.noteToggleEl.hidden = !overflows;
          this.noteToggleEl.textContent = this.noteToggleEl.dataset.readMore;
          this.noteToggleEl.setAttribute('aria-expanded', 'false');
        });
      }

      #toggleNote() {
        if (!this.noteEl || !this.noteToggleEl) return;
        const expanded = this.noteEl.classList.toggle('is-expanded');
        this.noteToggleEl.textContent = expanded ? this.noteToggleEl.dataset.readLess : this.noteToggleEl.dataset.readMore;
        this.noteToggleEl.setAttribute('aria-expanded', String(expanded));
      }

      // Cart line item property so the confirmed ride shows on the cart/order line,
      // matching the legacy fitment.js reference (a hidden properties[...] input tied
      // to the product form via its `form` attribute). We don't have a stable static
      // form id to target here (Horizon's product-form-component ids are section-
      // scoped), so we find the ATC form by its data-type instead and manage the
      // input directly. Passing null/empty removes it — never leave a stale vehicle
      // name attached once the ride is cleared or a different one is loading.
      #syncCartProperty(value) {
        // Scoped to this section, not document-wide — a recommendations/upsell
        // carousel elsewhere on the page can render its OWN add-to-cart-form for a
        // DIFFERENT product; a global querySelector could silently tag the wrong one.
        const scope = this.closest('.shopify-section') || document;
        const form = scope.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) return;
        const label = this.dataset.fitmentForLabel || 'Fitment for';
        const name = `properties[${label}]`;
        let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
        if (value) {
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            form.appendChild(input);
          }
          input.value = value;
        } else if (input) {
          input.remove();
        }
      }

      #buildShopUrl() {
        this.shopRideEl.href = this.#shopUrlFor(this.#levels.map((L) => ({ value: String(this.#str[this.#selected[L.oi]]) })));
      }

      // PLP filtered by a ride's fitment (filter.fitments=type>year>make>model).
      #shopUrlFor(parts) {
        const vals = (parts || []).map((p) => p.value);
        const base = this.dataset.shopUrl || '/collections/all';
        return `${base}?filter.fitments=${encodeURIComponent(vals.join('>'))}`;
      }

      // The selected Year level's string value, or null if none is selected yet.
      #currentYearValue() {
        const level = this.#levels.find((L) => L.tag === 'year');
        if (!level) return null;
        const idx = this.#selected[level.oi];
        if (idx == null) return null;
        const v = this.#str[idx];
        return v == null ? null : String(v);
      }

      // The Vehicle Information tab (snippets/pdp-vehicle-information.liquid,
      // rendered by Liquid elsewhere in this product section — see #atc() for the
      // same cross-component scoping reasoning) can contain maintenance links whose
      // filter.fitments query param (Type>Year>Make>Model) bakes in whatever year
      // the admin content happened to be written against. Keep them pointed at the
      // shopper's ACTUAL selected year instead of that stale hardcoded one.
      #updateVehicleInfoLinks() {
        const year = this.#currentYearValue();
        if (!year) return;
        const scope = this.closest('.shopify-section') || document;
        const container = scope.querySelector('.pdp-vehicle-info');
        if (!container) return;
        container.querySelectorAll('a[href*="filter.fitments"]').forEach((a) => {
          let url;
          try {
            url = new URL(a.getAttribute('href'), location.origin);
          } catch (e) {
            return; // malformed href — leave it alone
          }
          const fitments = url.searchParams.get('filter.fitments');
          if (!fitments) return;
          const parts = fitments.split('>');
          if (parts.length < 2) return; // not a Type>Year>... value — nothing to swap
          parts[1] = year;
          url.searchParams.set('filter.fitments', parts.join('>'));
          a.setAttribute('href', url.pathname + url.search);
        });
      }

      // ── variant selection (reuse Horizon's own change flow) ──────────────
      // The fitment table's variant_id column always holds a Shopify variant ID (we
      // publish that metafield) — never a SKU, so it's matched by id only.
      #variantById(id) {
        return this.#variants.find((v) => String(v.id) === String(id)) || null;
      }

      #selectVariantById(id) {
        const scope = this.closest('.shopify-section') || document;
        const radio = scope.querySelector(`variant-picker input[type="radio"][data-variant-id="${id}"]`);
        if (radio) {
          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return true;
        }
        const opt = scope.querySelector(`variant-picker option[data-variant-id="${id}"]`);
        if (opt) {
          const sel = opt.closest('select');
          if (sel) {
            sel.value = opt.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return true;
        }
        return this.#selectVariantByOptions(id);
      }

      // Multi-option products: an option value's data-variant-id only resolves against
      // the OTHER options currently selected, so a variant off the current row (e.g. the
      // 2.86" Pitch while 2.52" is selected — issue #142) has no input carrying its id.
      // Check that variant's value in EVERY option instead and fire a single change:
      // <variant-picker> posts all checked option_values together, so the morph lands on
      // exactly this variant and #onVariantUpdate re-runs #applyGate once it has.
      #selectVariantByOptions(id) {
        const variant = this.#variantById(id);
        const scope = this.closest('.shopify-section') || document;
        const picker = scope.querySelector('variant-picker');
        if (!variant || !Array.isArray(variant.options) || !picker) return false;

        // A single-value option (e.g. Construction) renders as plain text with no input, so
        // groups can be fewer than options — map each group to its option by the position
        // variant-main-picker.liquid stamps on it (data-fieldset-index / select id suffix).
        const groups = [...picker.querySelectorAll('fieldset, .variant-option--dropdowns')];
        if (!groups.length) return false;

        // Resolve every target first so a missing value never leaves a half-applied selection.
        const targets = groups.map((group) => {
          const position = group.matches('fieldset')
            ? Number(group.dataset.fieldsetIndex)
            : Number(group.querySelector('select')?.id.split('-').pop());
          if (!Number.isInteger(position) || variant.options[position] == null) return null;
          const want = String(variant.options[position]);
          const radio = [...group.querySelectorAll('input[type="radio"]')].find((r) => r.value === want);
          if (radio) return { radio };
          const select = group.querySelector('select');
          const option = select && [...select.options].find((o) => o.value === want);
          return option ? { select, option } : null;
        });
        if (targets.some((t) => !t)) return false;

        let changed = null;
        targets.forEach(({ radio, select, option }) => {
          if (radio) {
            if (radio.checked) return;
            radio.checked = true;
            changed = radio;
          } else if (!option.selected) {
            // selectedOptionsValues reads option[selected], not .value — move the attribute too.
            select.querySelectorAll('option[selected]').forEach((o) => o.removeAttribute('selected'));
            option.setAttribute('selected', 'selected');
            option.selected = true;
            changed = select;
          }
        });
        if (!changed) return true;

        changed.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      // ── Add-to-Cart gating ───────────────────────────────────────────────
      // Scoped to this section — see #syncCartProperty/#syncNativePicker for why a
      // document-wide query risks grabbing an unrelated form elsewhere on the page.
      #atc() {
        const scope = this.closest('.shopify-section') || document;
        return scope.querySelector('add-to-cart-component');
      }
      #forceAtc(enable) {
        const c = this.#atc();
        if (!c) return;
        if (enable && typeof c.enable === 'function') c.enable();
        else if (!enable && typeof c.disable === 'function') c.disable();
        const btn = c.querySelector('button[name="add"], button[type="submit"]');
        if (btn) btn.disabled = !enable; // fallback for either path
      }
      // Native <variant-picker> vs the fitment cascade are mutually exclusive, so ATC
      // gating follows the same split: in intro, Horizon's own product-form-component
      // already enables/disables ATC correctly off the native picker's selection — we
      // don't touch it at all. Once a ride starts, WE own it: disabled through the
      // quiz, enabled once a ride resolves to a variant that's ALSO actually in stock
      // — reaching "complete" only means a matching fitment row was found, which says
      // nothing about availability, so that alone must never force the button back on.
      #applyGate() {
        if (this.#view === 'intro') return;
        this.#forceAtc(this.#view === 'complete' && this.#resolvedAvailable && this.#variantFormSettled());
      }

      // #selectVariantById only DISPATCHES the change that tells Horizon's native
      // <variant-picker>/product-form to switch — that switch (updating the form's own
      // [name="id"] input, price, etc.) happens asynchronously in response. Enabling ATC
      // right away — as #applyGate used to, checking only #resolvedAvailable — raced
      // that: a shopper who clicked Add to Cart fast enough could submit the PREVIOUS
      // variant's id instead of the one the ride just resolved to. This makes that a
      // precondition, so #applyGate correctly stays disabled immediately after
      // #complete() dispatches the change and the form hasn't caught up yet — and
      // correctly enables once it has, via the existing #onVariantUpdate listener
      // (below) re-calling #applyGate on Horizon's own confirmation that it's done.
      #variantFormSettled() {
        if (!this.#resolvedVariantId) return false;
        const scope = this.closest('.shopify-section') || document;
        const idInput = scope.querySelector('[name="id"]');
        return !!idInput && String(idInput.value) === String(this.#resolvedVariantId);
      }

      // ── view state ───────────────────────────────────────────────────────
      #renderView(view) {
        this.#view = view;
        const intro = view === 'intro',
          ride = view === 'ride',
          complete = view === 'complete';
        this.titleEl.textContent = intro ? this.dataset.headingIntro || 'Your Garage' : this.dataset.headingRide || 'Your Ride';
        this.classList.toggle('is-intro', intro);
        this.classList.toggle('is-ride', ride);
        this.classList.toggle('is-complete', complete);
        this.introEl.hidden = !intro;
        this.cascadeEl.hidden = intro;
        // Clear Vehicle / Can't-find links stay available once a ride is underway —
        // through the quiz AND after it completes, so a fully-selected ride can still be
        // cleared or reported. Hidden only on the intro state.
        this.rideLinksEl.hidden = intro;
        // Shop Ride / Add To Your Garage are intentionally never shown: completing the
        // ride confirms fitment in place (green card + "Guaranteed fit for") rather than
        // sending the shopper elsewhere.
        this.completeEl.hidden = true;
        this.hintEl.hidden = !ride;
        // Native <variant-picker> vs the fitment cascade are mutually exclusive —
        // never both at once. Visible only in intro; hidden the instant a ride starts.
        this.#syncNativePicker(intro);
        this.statusText.textContent = complete ? GUARANTEED_TEXT : this.#statusDefault;
        if (!intro) this.#paintSteps();
        if (intro) {
          this.#closeGarageModal();
          this.#closeFindModal();
        }
        this.#applyGate(); // intro/complete → ATC active, ride → inactive
      }

      #paintSteps() {
        this.#levels.forEach((lvl, i) => {
          const node = this.#steps[i];
          const v = this.#selected[lvl.oi];
          const labelEl = q(node, '[data-smr-step-label]');
          if (v != null) {
            node.classList.add('is-selected');
            labelEl.textContent = String(this.#str[v]);
          } else {
            node.classList.remove('is-selected');
            labelEl.textContent = lvl.label;
          }
        });
        const active = this.#firstUnselected();
        this.#steps.forEach((n, i) => n.classList.toggle('is-active', this.#view === 'ride' && i === active));
        // "partial" = ride started and a step still needs a value (red current step +
        // hint) — including the very first step, so it's flagged exactly like every
        // later one instead of only once a choice has already been made.
        this.classList.toggle('is-partial', this.#view === 'ride' && active > -1);
      }

      #setHint(activeIdx) {
        // #advanceTo only ever calls this with the step that needs an actual choice
        // right now (including the very first one), so there's no "empty ride" case
        // left to special-case here — activeIdx is never negative in practice.
        if (activeIdx < 0) {
          this.hintEl.hidden = true;
          return;
        }
        this.hintEl.hidden = false;
        const label = this.#levels[activeIdx] ? this.#levels[activeIdx].label : '';
        this.hintEl.innerHTML = `Select <b>${escapeHtml(label)}</b> to ensure fitment.`;
      }

      #firstUnselected() {
        for (let i = 0; i < this.#levels.length; i++) if (this.#selected[this.#levels[i].oi] == null) return i;
        return -1;
      }

      // ── intro / clear ────────────────────────────────────────────────────
      #startRide() {
        this.#activeEntries = null; // manual build — no known vehicle to scope to
        this.#knownVehicle = false; // from-scratch build — #advanceTo must not auto-pick anything
        this.#resetLocks();
        this.#syncCartProperty(null); // no confirmed vehicle yet — don't carry a stale one
        this.#setNote(null); // no confirmed vehicle yet — don't carry a stale note either
        this.#advanceTo(0); // no single-option step auto-selects; shopper clicks every level
      }

      // On a /products/{handle}/fit-{vehicleId}-{slug} URL, the page's own title/
      // canonical/Vehicle Information tab are all vehicle-specific (see
      // blocks/dev-shop-my-ride.liquid + snippets/dev-fitment-vehicle-title.liquid).
      // A plain in-place #clear() would leave the shopper on that URL with nothing
      // selected — title still says "for {Make} {Model}" while the widget shows an
      // empty ride. Full-navigate back to the plain product URL instead, so the
      // page's own SEO content and the widget's state agree again.
      #clearOrLeaveFitUrl() {
        if (this.#fitVehicleId && this.dataset.productUrl) {
          location.href = this.dataset.productUrl;
          return;
        }
        this.#clear();
      }

      #clear() {
        this.#activeEntries = null;
        this.#knownVehicle = false;
        this.#selected = {};
        this.#resetLocks();
        this.#syncCartProperty(null);
        this.#setNote(null); // no ride active — don't leave a stale note showing
        this.#steps.forEach((_, k) => this.#closeStep(k));
        this.#renderView('intro'); // back to initial → native picker visible, cascade hidden
        this.#resyncNativeAtc(); // we may have forced ATC while a ride was active — hand state back to native
      }

      // ── accordion open/close ─────────────────────────────────────────────
      #openStep(i) {
        const n = this.#steps[i];
        q(n, '[data-smr-step-list]').hidden = false;
        q(n, '[data-smr-step-toggle]').setAttribute('aria-expanded', 'true');
        n.classList.add('is-open');
      }
      #closeStep(i) {
        const n = this.#steps[i];
        q(n, '[data-smr-step-list]').hidden = true;
        q(n, '[data-smr-step-toggle]').setAttribute('aria-expanded', 'false');
        n.classList.remove('is-open');
      }
      #toggleStep(i) {
        if (this.#lockedSteps.has(i)) return; // value fixed by the resolved vehicle — no manual override
        const open = this.#steps[i].classList.contains('is-open');
        this.#steps.forEach((_, k) => this.#closeStep(k));
        if (!open) {
          this.#populateStep(i);
          this.#openStep(i);
        }
      }

      // ── local garage (localStorage) + "My Garage" popup ──────────────────
      #readGarage() {
        try {
          return JSON.parse(localStorage.getItem(GARAGE_KEY)) || [];
        } catch {
          return [];
        }
      }
      #writeGarage(list) {
        try {
          localStorage.setItem(GARAGE_KEY, JSON.stringify(list));
        } catch {
          /* quota / private mode */
        }
        document.dispatchEvent(new CustomEvent('dev-garage:change')); // notify cart badges etc.
      }

      // Rides to show = the shopper's actually-saved garage (localStorage). No
      // sample fallback: the garage must match the site-wide "My Garage" popup,
      // which reads the same key with no demo rides. (The sample FITMENT matrix
      // for previewing the quiz is separate — see the sample_fitment in Liquid.)
      #garageVehicles() {
        return this.#readGarage();
      }
      #refreshGarageButton() {
        if (this.garageBtn) this.garageBtn.disabled = this.#garageVehicles().length === 0;
      }

      #addToGarage() {
        if (!this.#vehicleName) return;
        const parts = this.#levels.map((L) => ({ tag: L.tag, value: String(this.#str[this.#selected[L.oi]]) }));
        const list = this.#readGarage();
        if (!list.some((x) => x.name === this.#vehicleName)) {
          list.push({ name: this.#vehicleName, parts, sku: this.#resolvedSku });
          this.#writeGarage(list);
        }
        this.#refreshGarageButton();
        // Surface the saved ride immediately: open "My Garage" (the find modal)
        // so the shopper sees their garage with the just-added vehicle in it.
        this.#openFindModal();
      }

      // Fit of a saved ride vs THIS product = the deepest YMM prefix (type→year→make→
      // model) that any fitment row matches. Full depth → guaranteed, 0 → incompatible,
      // in between → partial. Labels are matched to this product's `str` case-insensitively.
      #strIndex(label) {
        const t = String(label).toLowerCase();
        for (let i = 0; i < this.#str.length; i++) if (String(this.#str[i]).toLowerCase() === t) return i;
        return -1;
      }

      // Resolve the incoming TYPE value to a str index, tolerating the picker's
      // detailed category vs the product's coarse type (e.g. incoming
      // "Motorcycle - Dual-Sport" → this product's "Motorcycle"). Exact match wins;
      // otherwise fall back to the coarse type ACTUALLY used by this product's
      // fitment rows whose ride family matches — returning the product's own str
      // index keeps the downstream cascade filtering correct.
      #typeStrIndex(value, typeOi) {
        const exact = this.#strIndex(value);
        if (exact >= 0) return exact;
        const fam = rideFamily(value);
        for (const e of this.#entries) {
          for (const ix of [e[typeOi]].flat(2)) {
            if (typeof ix === 'number' && rideFamily(this.#str[ix]) === fam) return ix;
          }
        }
        return -1;
      }

      // `parts` is LEVEL-ORDERED: parts[i] corresponds to #levels[i]. Both the incoming
      // deep link and garage rides are built in level order.
      //
      // Status classification only — used for garage-row coloring and the
      // incompatibility gate in #applyIncomingRide. Does NOT drive
      // the cascade itself (see #loadRide for that). ID-first: a vehicle_ids/
      // sbv_fitments hit is always "guaranteed" — the fitment feed already confirmed
      // this exact vehicle fits this exact product, even if more than one SKU
      // remains for the shopper to pick between (e.g. Model). Falls back to
      // #rideMatchByLabel's string-matched depth only when the vehicle isn't in
      // vehicle_ids at all. Mirrors the legacy fitment.js reference
      // (resolveVehicleFitments → loadFitmentByVehicle fallback).
      #rideStatus(parts) {
        if (this.#resolveVehicleFitments(parts)) return 'guaranteed';
        return this.#rideMatchByLabel(parts).status;
      }

      // Canonical id lookup: each level's label → a small integer code private to the
      // vehicle_ids metafield (vehicle_ids.str, case-insensitive) → composite key →
      // catalog-wide vehicleId (vehicle_ids.vehicle_ids). This id space is independent
      // of any product's own fitment `str` table, which is exactly why it's reliable
      // where per-product label matching isn't.
      //
      // The composite key is ALWAYS exactly Type>Year>Make>Model (YMM_TAGS.length
      // positions) — the fixed vehicle depth used everywhere else (Marqo fitmentKeys,
      // garage, deep links: filter.fitments is always 4 segments). A product's OWN
      // #levels can run longer than that — e.g. a trailing "Location: Front/Rear"
      // column — those are PART attributes, not vehicle attributes, and folding a
      // bogus extra code into the key (parts[4].value is undefined; no incoming deep
      // link ever supplies a 5th segment) made every lookup fail outright.
      #resolveVehicleId(parts) {
        const vids = this.#vehicleIds;
        if (!vids || !vids.str || !vids.vehicle_ids) return null;
        const ymmParts = (parts || []).slice(0, YMM_TAGS.length);
        if (ymmParts.length < YMM_TAGS.length) return null;
        const codes = ymmParts.map((p) => vids.str[String(p && p.value).toLowerCase()]);
        if (codes.some((c) => c == null)) return null;
        const id = vids.vehicle_ids[codes.join('>')];
        return id == null ? null : id;
      }

      // fitment.sbv_fitments[vehicleId] is THIS product's pre-computed list of
      // compatible fitment rows for that exact vehicle: [fitmentKey, fitYearIndex]
      // pairs. fitYearIndex resolves a row's year position (an array when one row
      // spans multiple years) down to the single year this vehicle actually is.
      // Returns the resolved row(s) (decompressed only at the year slot — other
      // slots are still str-indices), or null if this vehicle/product combo has no
      // precomputed match.
      #resolveVehicleFitments(parts) {
        const raw = this.#cfg.sbv_fitments;
        if (!raw) return null;
        const vehicleId = this.#resolveVehicleId(parts);
        if (vehicleId == null) return null;
        const sbv = raw[vehicleId]; // object key lookup — numeric id auto-coerces to string
        if (!sbv || !sbv.length) return null;

        const yearOi = this.#levels.find((L) => L.tag === 'year')?.oi;
        const fitmentByKey = this.#cfg.fitment;
        const resolved = sbv.map(([fitmentKey, fitYearIndex]) => {
          const entry = fitmentByKey && fitmentByKey[fitmentKey];
          if (!entry) return null;
          const out = [...entry];
          if (yearOi != null && Array.isArray(out[yearOi])) out[yearOi] = out[yearOi][fitYearIndex];
          return out;
        }).filter(Boolean);

        return resolved.length ? resolved : null;
      }

      // Fallback path — string-matches parts against THIS product's own fitment
      // table. The columns' tags are unreliable (frequently EMPTY or non-canonical —
      // many products tag only "Year"), so we align by POSITION and never by tag.
      // Results are keyed by each level's option index (oi), which is collision-free
      // even when several columns share an empty tag (tag-keyed maps silently merged
      // them → the original bug).
      #rideMatchByLabel(parts) {
        const idxByOi = {};
        this.#levels.forEach((L, i) => {
          const p = (parts || [])[i];
          const v = p && p.value;
          // Level 0 is always the vehicle TYPE (cascade contract): resolve by ride family
          // so the picker's detailed category ("Motorcycle - Dual-Sport") matches the
          // product's coarse type ("Motorcycle"). Deeper levels match exactly.
          idxByOi[L.oi] = (v == null || v === '') ? -1
            : (i === 0 ? this.#typeStrIndex(v, L.oi) : this.#strIndex(v));
        });
        let depth = 0;
        for (const e of this.#entries) {
          let d = 0;
          for (const L of this.#levels) {
            const idx = idxByOi[L.oi];
            if (idx < 0 || ![e[L.oi]].flat(2).includes(idx)) break;
            d++;
          }
          if (d > depth) depth = d;
          if (depth === this.#levels.length) break;
        }
        // idxByOi above was resolved per-level, independently — "does this raw label
        // exist ANYWHERE in this product's str table", not "does the joint prefix
        // match a real entry". That's decorrelated from `depth`: a common label (a
        // year, a make) routinely resolves to SOME index even past the point where
        // matching entries actually stop. Truncate everything past the true matched
        // depth back to -1, so #loadRide's "stop at first gap" fill (and therefore
        // every dropdown-filtering/auto-select step downstream) reflects a real
        // match, never a level that merely happens to share a label with an
        // unrelated entry.
        this.#levels.forEach((L, i) => {
          if (i >= depth) idxByOi[L.oi] = -1;
        });
        const status = depth === this.#levels.length ? 'guaranteed' : depth === 0 ? 'incompatible' : 'partial';
        return { status, idxByOi };
      }

      #chooseFromGarage() {
        const vehicles = this.#garageVehicles();
        if (!vehicles.length) return;
        this.#garageSelIdx = -1;
        this.garageSubmitEl.disabled = true;
        this.garageRowsEl.innerHTML = '';
        this.#garageRows = vehicles.map((veh, i) => {
          // Garage modal is a binary decision (pick this ride or don't) — no
          // "maybe" middle ground, so a partial match (some but not all YMM levels
          // confirmed) displays and behaves the same as incompatible: it isn't a
          // guaranteed fit, so it isn't presented as pickable. #rideStatus itself
          // stays 3-state (partial still means something for deep-link resume
          // elsewhere); this only affects how the garage row itself is shown.
          let status = this.#rideStatus(veh.parts);
          if (status === 'partial') status = 'incompatible';
          const row = this.garageRowTpl.content.firstElementChild.cloneNode(true);
          row.classList.add(`is-${status}`);
          q(row, '[data-smr-garage-icon]').innerHTML = this.#iconHtml(veh.parts);
          q(row, '[data-smr-garage-name]').innerHTML = veh.parts && veh.parts.length ? veh.parts.map((p) => `<span>${escapeHtml(p.value)}</span>`).join('') : escapeHtml(veh.name);
          if (status === 'incompatible') {
            row.setAttribute('aria-disabled', 'true');
            row.tabIndex = -1;
          } else {
            const pick = () => this.#selectGarageRow(i);
            row.addEventListener('click', pick);
            row.addEventListener('keydown', (ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') {
                ev.preventDefault();
                pick();
              }
            });
          }
          this.garageRowsEl.appendChild(row);
          return { veh, status, row };
        });
        this.modalEl.hidden = false;
      }

      #selectGarageRow(i) {
        this.#garageSelIdx = i;
        this.#garageRows.forEach((r, k) => r.row.classList.toggle('is-selected', k === i));
        this.garageSubmitEl.disabled = false;
      }

      #confirmGarage() {
        if (this.#garageSelIdx < 0) return;
        const { veh } = this.#garageRows[this.#garageSelIdx];
        this.#closeGarageModal();
        this.#loadRide(veh.parts);
      }

      #closeGarageModal() {
        if (this.modalEl) this.modalEl.hidden = true;
      }

      // Apply a chosen ride. Two paths:
      //
      // Known vehicle (vehicle_ids/sbv_fitments resolved): scope the ENTIRE cascade
      // to just the confirmed-compatible rows (#activeEntries) and hand off to
      // #advanceTo from level 0 with no restriction — every remaining row is
      // already vehicle-confirmed, so there's no "swap the vehicle" risk in
      // auto-selecting a unanimous level. #advanceTo (via #valuesForStep, now
      // scoped to #activeEntries) auto-selects/locks any level down to one option
      // and stops at a real, correctly-filtered dropdown wherever more than one
      // remains — e.g. Model, when a generic requested vehicle (a canonical
      // "Outlander 1000") maps to several real SKUs (DPS/XMR/XT/XTP).
      //
      // Unknown vehicle (fallback): string-match against the FULL per-product
      // table, fill the matched PREFIX only, then hand off to #advanceTo — which
      // completes if the whole cascade resolved, otherwise drops into the quiz at
      // the first unmatched step (auto-skipping single-option steps beyond it).
      #loadRide(parts) {
        const resolved = this.#resolveVehicleFitments(parts);
        this.#activeEntries = resolved || null;
        // A real vehicle is being loaded either way — ID-confirmed (resolved) or
        // label-matched fallback below both count; #advanceTo's auto-select may run.
        this.#knownVehicle = true;
        this.#selected = {};
        this.#resetLocks();
        this.#syncCartProperty(null); // clear any prior ride's property — #complete() re-sets it if this one resolves fully
        this.#setNote(null); // same reasoning — #complete() re-sets it if this one resolves fully
        this.#steps.forEach((_, k) => this.#closeStep(k));

        if (resolved) {
          this.#advanceTo(0, 0);
          return;
        }

        const { idxByOi } = this.#rideMatchByLabel(parts);
        for (const L of this.#levels) {
          const idx = idxByOi[L.oi];
          if (idx < 0) break; // stop at first gap → keeps a valid cascade prefix
          this.#selected[L.oi] = idx;
        }
        const next = this.#firstUnselected();
        if (next < 0) {
          this.#complete(); // whole cascade already resolved from the deep link
          return;
        }
        // Only levels 0..next-1 actually MATCHED the requested vehicle — those must
        // not auto-substitute a different option (that would silently swap the
        // vehicle → false "Guaranteed fit"). `next` itself is exactly that matched
        // depth. Levels from `next` onward (this product simply doesn't have the
        // requested value there) are free to auto-select or filter down to their
        // one remaining compatible option.
        this.#advanceTo(next, next);
      }

      // ── "My Garage" management popup (Can't find your vehicle) ───────────
      #openFindModal() {
        this.#findVehicles = this.#garageVehicles().slice();
        this.#renderFindRows();
        this.findModalEl.hidden = false;
      }

      #renderFindRows() {
        this.findRowsEl.innerHTML = '';
        // No rides → hide the list entirely so the empty state keeps a single 20px
        // rhythm between the benefits and "Add Your Ride" (the design's empty frame).
        this.findRowsEl.hidden = this.#findVehicles.length === 0;
        this.#findVehicles.forEach((veh, i) => {
          const row = this.findRowTpl.content.firstElementChild.cloneNode(true);
          q(row, '[data-smr-find-icon]').innerHTML = this.#iconHtml(veh.parts);
          q(row, '[data-smr-find-name]').innerHTML = (veh.parts && veh.parts.length)
            ? veh.parts.map((p) => `<span>${escapeHtml(p.value)}</span>`).join('')
            : escapeHtml(veh.name);
          q(row, '[data-smr-find-shop]').href = this.#shopUrlFor(veh.parts);
          q(row, '[data-smr-find-clear]').addEventListener('click', () => this.#clearRide(i));
          this.findRowsEl.appendChild(row);
        });
      }

      #clearRide(i) {
        const veh = this.#findVehicles[i];
        if (!veh) return;
        this.#writeGarage(this.#readGarage().filter((x) => x.name !== veh.name)); // persisted rides only
        this.#findVehicles.splice(i, 1);                                          // sample rides too
        this.#renderFindRows();
        this.#refreshGarageButton();
      }

      // "Add Your Ride" → close the popup and start the quiz to build a new ride
      // (which can then be saved via "Add To Your Garage" on completion).
      #addYourRide() {
        this.#closeFindModal();
        this.#startRide();
      }

      #closeFindModal() {
        if (this.findModalEl) this.findModalEl.hidden = true;
      }

      // ── util ─────────────────────────────────────────────────────────────
      #sort(a, b, year) {
        const num = (s) => {
          const m = String(s).match(/\d+/);
          return m ? +m[0] : NaN;
        };
        const na = num(a),
          nb = num(b);
        if (!isNaN(na) && !isNaN(nb) && na !== nb) return year ? nb - na : na - nb; // years high→low
        return year ? String(b).localeCompare(a) : String(a).localeCompare(b);
      }
    },
  );
})();
